import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, FileDown, Download } from "lucide-react";

const TIPI = [["servizio", "Servizio"], ["marketing", "Marketing"], ["profilazione", "Profilazione"], ["terzi", "Comunicazione a terzi"]];

// Carica la mappa dei consensi marketing per una lista di contatti (solo Direzione/Segreteria).
export function useMarketingMap(contactType) {
  const { isDirezione, isSegreteria } = useAuth();
  const [map, setMap] = useState(null);
  useEffect(() => {
    if (isDirezione || isSegreteria) {
      api.get(`/gdpr/marketing-map/${contactType}`).then((r) => setMap(r.data || {})).catch(() => setMap({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactType, isDirezione, isSegreteria]);
  return map;
}

// Badge "Marketing OK / —" da mostrare accanto a un contatto nelle liste.
export function MarketingBadge({ map, id }) {
  if (!map) return null;
  const ok = !!map[id];
  return (
    <span data-testid={`marketing-badge-${id}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${ok
        ? "text-emerald-600 bg-emerald-400/10 border-emerald-400/30"
        : "text-slate-500 bg-slate-500/10 border-slate-400/30"}`}>
      <ShieldCheck size={11} /> Marketing {ok ? "OK" : "—"}
    </span>
  );
}

// Esporta la lista contatti in CSV scegliendo lo scopo (operativo | marketing).
// Scopo marketing → il backend esporta SOLO chi ha consenso marketing attivo e registra l'audit.
export function ExportListaButton({ contactType }) {
  const { isDirezione, isSegreteria } = useAuth();
  const [open, setOpen] = useState(false);
  if (!(isDirezione || isSegreteria)) return null;
  const run = async (scopo) => {
    setOpen(false);
    try {
      const res = await api.get(`/gdpr/export-lista/${contactType}?scopo=${scopo}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `lista_${contactType}_${scopo}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(scopo === "marketing" ? "Esportati solo i contatti con consenso marketing." : "Lista esportata.");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="relative">
      <button type="button" data-testid="export-lista-btn" onClick={() => setOpen((o) => !o)}
        className="px-3.5 py-2 rounded-lg text-xs font-semibold border border-[var(--border)] text-slate-600 hover:border-[#FC82ED]/50 flex items-center gap-1.5">
        <Download size={14} /> Esporta lista
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-20 w-60 rounded-lg border border-[var(--border)] bg-white shadow-lg p-1" data-testid="export-lista-menu">
          <button type="button" data-testid="export-lista-operativo" onClick={() => run("operativo")}
            className="w-full text-left text-xs px-3 py-2 rounded-md hover:bg-slate-50">Scopo operativo <span className="text-[var(--muted)]">· tutti i contatti</span></button>
          <button type="button" data-testid="export-lista-marketing" onClick={() => run("marketing")}
            className="w-full text-left text-xs px-3 py-2 rounded-md hover:bg-slate-50">Scopo marketing <span className="text-[var(--muted)]">· solo consenso attivo</span></button>
        </div>
      )}
    </div>
  );
}

export default function GdprContactPanel({ contactType, contactId }) {
  const { isDirezione, isSegreteria } = useAuth();
  const [data, setData] = useState(null);
  const [modalita, setModalita] = useState("firmato");

  const load = () => api.get(`/gdpr/consensi/${contactType}/${contactId}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (contactId) load(); /* eslint-disable-next-line */ }, [contactId]);

  if (!(isDirezione || isSegreteria) || !contactId) return null;

  const setConsent = async (tipo, stato) => {
    try { await api.post("/gdpr/consensi", { contact_type: contactType, contact_id: contactId, tipo, stato, modalita }); toast.success("Consenso registrato."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportData = async (format) => {
    try {
      const res = await api.get(`/gdpr/export/${contactType}/${contactId}?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `gdpr_${contactType}_${contactId}.${format === "pdf" ? "pdf" : "json"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return null;
  return (
    <div className="mt-5 border-t border-[var(--border)] pt-4" data-testid="gdpr-contact-panel">
      <div className="flex items-center gap-2 mb-3"><ShieldCheck size={16} className="text-[#FC82ED]" /><span className="text-sm font-semibold">Privacy & consensi (GDPR)</span></div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-[var(--muted)]">Modalità di raccolta:</span>
        <select data-testid="gdpr-modalita" value={modalita} onChange={(e) => setModalita(e.target.value)} className="text-xs px-2 py-1 rounded border border-[var(--border)]">
          {["firmato", "verbale", "online"].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div className="space-y-2">
        {TIPI.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between">
            <span className="text-sm">{label}{k === "marketing" && !data.current.marketing && <span className="ml-2 text-[11px] text-red-500">· invii marketing bloccati</span>}</span>
            <div className="flex gap-1">
              <button type="button" data-testid={`gdpr-consent-${k}-si`} onClick={() => setConsent(k, true)} className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${data.current[k] ? "bg-emerald-500 text-white border-emerald-500" : "border-[var(--border)] text-[var(--muted)]"}`}>Sì</button>
              <button type="button" data-testid={`gdpr-consent-${k}-no`} onClick={() => setConsent(k, false)} className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${!data.current[k] ? "bg-slate-700 text-white border-slate-700" : "border-[var(--border)] text-[var(--muted)]"}`}>No</button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <button type="button" data-testid="gdpr-export-pdf" onClick={() => exportData("pdf")} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-[var(--border)] hover:border-[#FC82ED] transition-colors"><FileDown size={14} />Esporta dati (PDF)</button>
        <button type="button" data-testid="gdpr-export-json" onClick={() => exportData("json")} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-[var(--border)] hover:border-[#FC82ED] transition-colors"><FileDown size={14} />Esporta dati (JSON)</button>
      </div>
    </div>
  );
}
